import React, { useState, useEffect, useRef, useCallback } from "react";
import CookieConsent from "../components/CookieConsent";
import Footer from "../components/Footer";
import Header from "../components/Header";
import "../App.css";
import { loadGoogleAdSense, loadGoogleAnalytics } from "../helpers/loadScripts";
import { countChar, getByteCount, getReadingTime } from "../helpers/text";
import { createSwapy } from 'swapy';
function TextCounter({cookieConsent, setCookieConsent}) {
  const [text, setText] = useState(localStorage.getItem("text") || "");
  const [countSpaces, setCountSpaces] = useState(
    localStorage.getItem("countSpaces") !== "false"
  );
  const [countLineBreaks, setCountLineBreaks] = useState(
    localStorage.getItem("countLineBreaks") !== "false"
  );
  const [speed, setSpeed] = useState(
    localStorage.getItem("speed") || 300
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [paragraphMode, setParagraphMode] = useState(
    localStorage.getItem("paragraphMode") || 'single'
  );
  const [encoding, setEncoding] = useState(
    localStorage.getItem("encoding") || 'UTF-8'
  );
  const [segments, setSegments] = useState([]);

  const swapy = useRef(null);
  const container = useRef(null);
  const paragraphModeRef = useRef(paragraphMode);

  const maxLength = 100000;
  const clientId = "ca-pub-6879724800674951";
  const trackingId = "G-6C60QL7MSB";

  useEffect(() => {
    localStorage.setItem("text", text);
  }, [text]);

  useEffect(() => {
    paragraphModeRef.current = paragraphMode;
  }, [paragraphMode]);

  // クッキーの同意状態を取得
  useEffect(() => {
    if (cookieConsent === "accepted") {
      loadGoogleAdSense(clientId);
      loadGoogleAnalytics(trackingId);
    }
  }, [cookieConsent]);

  // 設定変更時にlocalStorageに保存するuseEffect
  useEffect(() => {
    localStorage.setItem("countSpaces", countSpaces);
    localStorage.setItem("countLineBreaks", countLineBreaks);
    localStorage.setItem("paragraphMode", paragraphMode);
    localStorage.setItem("encoding", encoding);
    localStorage.setItem("speed", speed);
  }, [countSpaces, countLineBreaks, paragraphMode, encoding, speed]);

  useEffect(() => {
    const swapText = (from, to) => {
      setText(prevText => {
        const newText = [...prevText];
        const separator = paragraphModeRef.current === 'double' ? '\n\n' : '\n';
        const newTextParagraphs = newText.join('').split(separator);
        [newTextParagraphs[from], newTextParagraphs[to]] = [newTextParagraphs[to], newTextParagraphs[from]];
        return newTextParagraphs.join(separator);
      });
    }

    if (container.current) {
      swapy.current = createSwapy(container.current);
      swapy.current.onSwap((event) => {
        swapText(event.fromSlot, event.toSlot);
      })
    }

    return () => {
      swapy.current?.destroy();
    };
  }, []);

  const styles = {
    container: {
      paddingBottom: "100px",
      paddingTop: "70px",
      minHeight: "100vh",
      position: "relative",
    },
    spacer: {
      height: "70px",
      backgroundColor: "#f0f0f0",
      marginTop: "70px",
      width: "100%"
    },
    logo: {
      width: "40px",
      height: "40px",
      marginRight: "5px",
      paddingLeft: "10px",
      '@media (maxWidth: 768px)': {
        width: "30px",
        height: "30px",
        paddingLeft: "5px",
      }
    },
    header: {
      width: "100%",
      height: "70px",
      boxShadow: "0 0 20px 0 rgba(0, 0, 0, 0.3)",
      display: "flex",
      alignItems: "center",
      position: "fixed",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 1000,
      '@media (maxWidth: 768px)': {
        height: "60px",
      }
    },
    headerText: {
      textAlign: "left",
      height: "100%",
      fontSize: "20px"
    },
    brandText: {
      paddingLeft: "10px",
      color: "#32bc9b",
      fontWeight: "bold",
      fontSize: "20px",
      '@media (maxWidth: 768px)': {
        fontSize: "16px",
        paddingLeft: "5px",
      }
    },
    textareaContainer: {
      width: "90%",
      maxWidth: "1200px",
      margin: "0 auto",
      backgroundColor: "#fff",
      borderRadius: "20px",
      boxShadow: "0 0 20px 0 rgba(0, 0, 0, 0.3)",
      '@media (maxWidth: 768px)': {
        width: "95%",
        borderRadius: "10px",
      }
    },
    textarea: {
      width: "90%",
      maxWidth: "97%",
      minHeight: "30vh",
      height: "70%",
      margin: "3% auto",
      padding: "10px",
      border: "none",
      outline: "none",
      boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.3)",
      borderRadius: "10px"
    },
    settings: {
      margin: "auto 10px",
      paddingBottom: "10px"
    },
    countContainer: {
      margin: "auto 20px",
      paddingBottom: "10px",
      '@media (maxWidth: 768px)': {
        margin: "auto 10px",
        fontSize: "14px",
      }
    },
    adsenseContainer: {
      marginTop: "30px",
    },
    adsense: {
      display: "block",
      margin: "auto",
      width: "90%",
      maxWidth: "97%",
      dataAdClient: "",
      dataAdSlot: "",
      dataAdFormat: "auto",
      dataFullWidthResponsive: true
    },
    cookieReset: {
      color: "#32bc9b",
      border: "none",
      borderRadius: "5px",
      position: "absolute",
      right: "30px",
      bottom: "30px",
    },
    settingsToggle: {
      background: 'none',
      border: 'none',
      color: '#32bc9b',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      padding: '5px',
      fontSize: '16px',
    },
    settingsContent: {
      maxHeight: isSettingsOpen ? '500px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.3s ease-in-out',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '@media (maxWidth: 768px)': {
        alignItems: 'flex-start',
        '& > div': {
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          marginBottom: '10px'
        },
        '& label': {
          display: 'block',
          marginLeft: '0',
          marginTop: '10px',
          width: '100%'
        },
        '& select': {
          display: 'block',
          margin: '5px 0',
          width: '200px',
          maxWidth: '100%'
        }
      }
    },
    resetButton: {
      background: 'none',
      border: '1px solid #32bc9b',
      borderRadius: '5px',
      color: '#32bc9b',
      cursor: 'pointer',
      padding: '5px 10px',
      marginLeft: '10px',
    },
    encodingSelect: {
      marginLeft: '0.5rem',
      marginTop: '10px',
      marginBottom: '10px',
      marginRight: '10px',
      padding: '5px 10px',
      border: '1px solid #32bc9b',
      borderRadius: '5px',
      color: '#32bc9b',
      cursor: 'pointer',
    },
    segmentSelect: {
      marginLeft: '0.5rem',
      marginTop: '10px',
      marginBottom: '10px',
      marginRight: '10px',
      padding: '5px 10px',
      border: '1px solid #32bc9b',
      borderRadius: '5px',
      color: '#32bc9b',
      cursor: 'pointer',
    },
    speedSelect: {
      marginLeft: '0.5rem',
      marginTop: '10px',
      marginBottom: '10px',
      marginRight: '10px',
      padding: '5px 10px',
      border: '1px solid #32bc9b',
      borderRadius: '5px',
      color: '#32bc9b',
      cursor: 'pointer',
    },
    segmentPreviewContainer: {
      width: "95%",
      maxWidth: "97%",
      margin: "20px auto",
      borderRadius: "10px",
      boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.3)",
      padding: "15px",
      '@media (maxWidth: 768px)': {
        padding: "10px",
        fontSize: "14px",
      }
    },
    segment: {
      padding: "10px",
      margin: "5px 0",
      borderRadius: "5px",
      border: "1px solid #e0e0e0",
      fontSize: "14px",
      lineHeight: "1.5",
      textAlign: "left",
    },
    checkboxContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '10px',
      justifyContent: 'center',
      '@media (maxWidth: 768px)': {
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }
    },
    selectContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '10px',
      justifyContent: 'center',
      '@media (maxWidth: 768px)': {
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }
    },
  };

  // 文字数をカウント
  const charCount = countChar(text, countSpaces, countLineBreaks);

  // 文字コードに応じたバイト数をカウント
  // Shift_JISの場合の概算
  // バイト数をカウント
  const byteCount = getByteCount(
    text.split('').filter(char => {
      if (!countSpaces && (char === ' ' || char === '\u3000')) return false;
      if (!countLineBreaks && (char === '\n' || char === '\r')) return false;
      return true;
    }).join(''),
    encoding
  );

  // テキストエリアの変更を受け取る
  const handleChange = (e) => {
    setText(e.target.value);
  };

  // テキストをリセットする関数を追加
  const handleReset = () => {
    setText('');
  };

  const handleSegmentChange = (index, newContent) => {
    const segments = getSegments(text, paragraphMode);
    const newSegments = [...segments];
    newSegments[index] = newContent;
    
    const newText = paragraphMode === 'double' 
      ? newSegments.join('\n\n')
      : newSegments.join('\n');
    
    setText(newText);
  };

  const getSegments = useCallback((text, paragraphMode) => {
    if (!text) return [];
    if (paragraphMode === 'double') {
      return text
        .split(/\r?\n\s*\r?\n/)
        .filter(seg => seg.trim() !== "");
    } else {
      return text
        .split(/\r?\n/)
        .filter(seg => seg.trim() !== "" || countLineBreaks);
    }
  }, [countLineBreaks]);

  useEffect(() => {
    setSegments(getSegments(text, paragraphMode));
  }, [text, paragraphMode, getSegments]);

  // 今のモードが"行"なのか"段落"なのかでラベル変更してもOK
  const segmentLabel = (paragraphMode === 'double') ? "段落数" : "行数";


  // 読み上げ時間を分秒形式に変換する関数を追加
  const formatReadingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (minutes === 0) {
      return `${remainingSeconds}秒`;
    }
    return `${minutes}分${remainingSeconds}秒`;
  };

  return (
    <div className="App" style={styles.container}>
      <Header />
      <div style={styles.textareaContainer}>
        <textarea
          value={text}
          onChange={handleChange}
          rows={8}
          maxLength={maxLength}
          style={styles.textarea}
          placeholder="ここにテキストを入力してください"
          disabled={cookieConsent !== "accepted"}
        />
        <div style={styles.countContainer}>
          <p>文字数 : {charCount}</p>
          <p>バイト数({encoding}{encoding === 'UTF-8' ? '' : ':概算'}) : {byteCount}</p>
          <p>{segmentLabel} : {segments.length}</p>
          <p>読み上げ時間 : 約{formatReadingTime(getReadingTime(text, speed))}</p>
          <button
            style={styles.resetButton}
            onClick={handleReset}
            disabled={cookieConsent !== "accepted"}
          >
            テキストをリセット
          </button>
          <div style={styles.settings}>
            <button 
              style={styles.settingsToggle}
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
              disabled={cookieConsent !== "accepted"}
            >
              {isSettingsOpen ? '▼' : '▶'} カウント設定
            </button>
            <div style={styles.settingsContent}>
              <div style={styles.checkboxContainer}>
                <label>
                  <input
                    type="checkbox"
                    checked={countSpaces}
                    onChange={(e) => setCountSpaces(e.target.checked)}
                    disabled={cookieConsent !== "accepted"}
                  />
                  スペースをカウント
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={countLineBreaks}
                    onChange={(e) => setCountLineBreaks(e.target.checked)}
                    disabled={cookieConsent !== "accepted"}
                  />
                  改行をカウント
                </label>
              </div>
              <div style={styles.selectContainer}>
                <label>
                  行数カウント
                  <select
                    style={styles.segmentSelect}
                    value={paragraphMode}
                    onChange={(e) => setParagraphMode(e.target.value)}
                    disabled={cookieConsent !== "accepted"}
                  >
                    <option value="single">1行区切り</option>
                    <option value="double">空行区切り</option>
                  </select>
                </label>
                <label>
                  文字コード
                  <select
                    style={styles.encodingSelect}
                    value={encoding}
                    onChange={(e) => setEncoding(e.target.value)}
                    disabled={cookieConsent !== "accepted"}
                  >
                    <option value="UTF-8">UTF-8</option>
                    <option value="Shift_JIS">Shift-JIS</option>
                    <option value="EUC-JP">EUC-JP</option>
                  </select>
                </label>
                <label>
                  読み上げ速度
                  <select
                    style={styles.speedSelect}
                    value={speed}
                    onChange={(e) => setSpeed(e.target.value)}
                    disabled={cookieConsent !== "accepted"}
                  >
                    <option value="200">ゆっくり</option>
                    <option value="300">普通</option>
                    <option value="400">速読</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
      <div style={styles.segmentPreviewContainer} ref={container}>
        <p>{segmentLabel}: {segments.length}</p>
        {segments.map((seg, idx) => {
          const segmentCharCount = countChar(seg, countSpaces, countLineBreaks);
          const segmentByteCount = getByteCount(
            seg.split('').filter(char => {
              if (!countSpaces && (char === ' ' || char === '\u3000')) return false;
              if (!countLineBreaks && (char === '\n' || char === '\r')) return false;
              return true;
            }).join(''),
            encoding
          );

          const bgColor = (idx % 2 === 0) ? "#f0f8f8" : "#ffffff";
          return (
          <div data-swapy-slot={idx}>
            <div
              key={idx}
              style={{ 
                ...styles.segment, 
                backgroundColor: bgColor,
                position: 'relative',
                paddingBottom: '2.5em'
              }}
              data-swapy-item={idx}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ 
                  minWidth: '30px',
                  color: '#666',
                  textAlign: 'right',
                  marginRight: '10px'
                }}>
                  {idx + 1}.
                </span>
                <textarea
                  value={seg}
                  onChange={(e) => handleSegmentChange(idx, e.target.value)}
                  disabled={cookieConsent !== "accepted"}
                  style={{ 
                    outline: 'none',
                    fieldSizing: 'content',
                    border: 'none',
                    resize: 'none',
                    flex: 1,
                    background: 'transparent',
                    padding: '2px 4px',
                    width: '100%'
                  }}
                />
              </div>
              <div style={{
                position: 'absolute',
                bottom: '5px',
                right: '10px',
                fontSize: '0.85em',
                color: '#666',
                display: 'flex',
                gap: '15px'
              }}>
                <span>
                  {segmentCharCount}文字 / {segmentByteCount}バイト
                </span>
                <span>
                  読み上げ時間: {formatReadingTime(getReadingTime(seg, speed))}
                </span>
              </div>
            </div>
          </div>
          );
        })}
      </div>
        </div>
      </div>
      <CookieConsent cookieConsent={cookieConsent} setCookieConsent={setCookieConsent} />
      <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6879724800674951"
        crossOrigin="anonymous"></script>
      <ins className="adsbygoogle"
          style={{display: 'block'}}
          data-ad-client="ca-pub-6879724800674951"
          data-ad-slot="8576911902"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins>
      <script>
          (adsbygoogle = window.adsbygoogle || []).push({});
      </script>
      <Footer cookieConsent={cookieConsent} setCookieConsent={setCookieConsent} />
    </div>
  );
}

export default TextCounter;
